// ============================================================================
// FILE: frontend/src/pages/TasksPage.jsx
// Container for the Tasks grid. Presentational pieces, the Other-tab grid,
// the modals and the column defs live in ./tasks/.
// ============================================================================

import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import RollForwardModal from "../components/rollforward/RollForwardModal";
import RollForwardNudge from "../components/rollforward/RollForwardNudge";
import VirtualTableRows from "../components/VirtualTableRows";
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from "../api";
import {
  getTaskTypes,
  bulkAssignTaskType,
  deleteTask,
  resolveClientGroup,
  updateTask,
  apiErrorMessage,
} from "../api";
import { Link, useNavigate, useLocation } from "react-router-dom";
import FileUploadModal from "../components/FileUploadModal";
import SharePointFilePicker from "../components/SharePointFilePicker";
import LoadingSpinner from "../components/LoadingSpinner";
import ClientUpdatesPanel from "../components/ClientUpdatesPanel";
import { formatDateForInput, monthsUntil } from '../utils/DateUtils';

import {
  extractMentions,
  MONDAY_COLORS,
  isCorporate,
  taskLabel,
} from "./tasks/constants";
import OwnerBubble from "./tasks/OwnerBubble";
import SortableHeader from "./tasks/SortableHeader";
import ResizeHandle from "./tasks/ResizeHandle";
import NewClientModal from "./tasks/NewClientModal";
import { addMasterFile } from "../components/masterFile";
import OtherTasksTable from "./tasks/OtherTasksTable";
import CreateTaskModal from "./tasks/CreateTaskModal";
import NewTaskTypeForm from "./tasks/NewTaskTypeForm";
import CheckboxCell from "./tasks/CheckboxCell";
import FlagCell from "./tasks/FlagCell";
import { BASE_COLUMNS, DEFAULT_COLUMN_WIDTHS, renderedColumnKeys } from "./tasks/columns";
import {
  filterRowsPreTab,
  filterRowsByTab,
  sortRows,
  yearEndMonthOf,
} from "./tasks/taskFilters";
import TaskFilterBar, { DEFAULT_HIDDEN_STATUSES, MONTHS } from "./tasks/TaskFilterBar";
import useTasksData, { normalizeRows } from "./tasks/useTasksData";
import RollForwardFlow from "./tasks/RollForwardFlow";
import NextYearCell from "./tasks/NextYearCell";
import OwnerPickerModal from "./tasks/OwnerPickerModal";
import { isBookkeepingTask } from "./tasks/bookkeeping";
import { loadDefaultView, saveDefaultView, clearDefaultView } from "./tasks/defaultView";
import { planTslipUncheck, SLIP_FIELDS, SLIP_LABELS } from "./tasks/tslipMirror";

// Always offered in the toolbar's Fiscal year list, even before any task has
// been stamped with it. The grid itself opens on "All years": with Completed
// hidden by default, every year's open work is the useful first view, and a
// file just rolled forward into next year stays in sight. Read once at module
// load -- a session open across New Year's Eve keeps it until reload.
const CURRENT_FISCAL_YEAR = String(new Date().getFullYear());

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// The Tasks page tabs. TAB_KEYS is the whitelist for the ?tab= query param,
// so a link can land on a specific tab.
const TABS = [
  { key: "personal", label: "Personal" },
  { key: "corporate", label: "Corporate" },
  { key: "tslips", label: "T Slips" },
  { key: "bookkeeping", label: "Bookkeeping" },
  { key: "other", label: "Other" },
  { key: "all", label: "All" },
];
const TAB_KEYS = TABS.map((t) => t.key);

// The Filter panel's values when nothing is chosen. Also the built-in view
// that "Reset" (beside "Save as my default view") returns to.
const INITIAL_FILTERS = {
  group: "",
  owner: "",
  priority: "",
  type: "",
  fiscalYear: "",
  yearEndMonth: "",
  nextYear: "",
  t4_status: "",
  t5_status: "",
  gst_status: "",
  cap_div_status: "",
  cra_auth_status: "",
  efile_number: "",
  efile_date: "",
  personal_gst_status: "",
  t1135_status: "",
  cda_status: "",
  cra_auth_personal: "",
};
const INITIAL_CLIENT_STATUSES = ["active", "completed"];
// Tabs that show Other tasks (OtherTasksTable) rather than the main grid.
const OTHER_KINDS = new Set(["bookkeeping", "other"]);

export default function TasksPage({ user }) {
  const navigate = useNavigate();

  // State
  const [query, setQuery] = useState("");
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [sharePointPickerOpen, setSharePointPickerOpen] = useState(false);
  const [selectedClientForFiles, setSelectedClientForFiles] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [editingTaskCell, setEditingTaskCell] = useState(null);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [selectedClientForOwner, setSelectedClientForOwner] = useState(null);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [openStatusMenu, setOpenStatusMenu] = useState(null); // Now stores { key, rect }
  const [showUpdatesPanel, setShowUpdatesPanel] = useState(false);
  const [selectedClientForUpdates, setSelectedClientForUpdates] =
    useState(null);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [editingGroupClientId, setEditingGroupClientId] = useState(null);
  const [editingGroupValue, setEditingGroupValue] = useState("");
  // Guards handleGroupSave against double-submit: it is reachable from both
  // onBlur and the Enter keydown handler, and pressing Enter then clicking
  // away fires both without this.
  const groupSaveInFlightRef = useRef(false);
  const scrollRef = useRef(null);
  // This person's saved "default view" (./tasks/defaultView), read once on
  // open. null = nothing saved, and the page opens exactly as it always has.
  const [savedView] = useState(() => loadDefaultView(user?.id, {
    tabKeys: TAB_KEYS, filterKeys: Object.keys(INITIAL_FILTERS),
  }));
  const [viewNotice, setViewNotice] = useState("");

  // The tab can be targeted from the URL (?tab=tslips), which always wins.
  // Then a saved default view's tab -- "how it opens whenever I open it" --
  // and only then the last tab this browser session was on.
  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("tab");
    if (fromUrl && TAB_KEYS.includes(fromUrl)) return fromUrl;
    if (savedView?.tab) return savedView.tab;
    return sessionStorage.getItem("clientsPageActiveTab") || "personal";
  });

  // Following a ?tab= link while already on /tasks is a client-side
  // navigation: the component does NOT remount, so the useState initializer
  // above never re-runs. Watch the query string instead.
  const location = useLocation();
  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get("tab");
    if (fromUrl && TAB_KEYS.includes(fromUrl)) {
      setActiveTab(fromUrl);
    }
  }, [location.search]);

  // Nothing is ever truly hidden -- this chip brings forward-planned work
  // back into the grid. Off by default so the daily view stays the day's work.
  const [includePlanning, setIncludePlanning] = useState(
    () => savedView?.includePlanning ?? false);

  // Everything loads once, on mount, in ./tasks/useTasksData — switching tabs
  // issues no network requests. It returns the setters too, because the grid
  // does optimistic updates before refetching.
  const {
    clients,
    setClients,
    allClients,
    invoices,
    employees,
    statuses,
    err,
    setErr,
    loading,
    refetch: fetchClients,
    otherTasks,
    setOtherTasks,
    otherTaskTypes,
    setOtherTaskTypes,
    otherLoading,
    reloadOtherTasks,
  } = useTasksData(includePlanning ? "all" : "active");
  const [filters, setFilters] = useState(
    () => ({ ...INITIAL_FILTERS, ...(savedView?.filters || {}) }));
  const [showFilters, setShowFilters] = useState(false);
  // The toolbar's Status checklist: keys it has unticked (see
  // taskFilters.statusKeyOf). Completed is hidden until someone ticks it.
  const [hiddenStatuses, setHiddenStatuses] = useState(
    () => new Set(savedView?.hiddenStatuses ?? DEFAULT_HIDDEN_STATUSES));

  // ----- New toolbar state -----
  const [personFilter, setPersonFilter] = useState(
    () => new Set(savedView?.personFilter ?? []));
  const [showPersonMenu, setShowPersonMenu] = useState(false);
  const [clientStatusFilterSet, setClientStatusFilterSet] = useState(
    () => new Set(savedView?.clientStatuses ?? INITIAL_CLIENT_STATUSES)
  );

  // "Save as my default view" / "Reset". Reset forgets the saved view and
  // puts the filters back to the built-in ones, but leaves the tab alone --
  // yanking you off the tab you are looking at would read as a bug.
  const saveAsDefaultView = () => {
    const ok = saveDefaultView(user?.id, {
      tab: activeTab, filters, hiddenStatuses, personFilter,
      clientStatuses: clientStatusFilterSet, includePlanning,
    });
    setViewNotice(ok ? "Saved: Tasks will open like this." : "Could not save in this browser.");
  };
  const resetDefaultView = () => {
    clearDefaultView(user?.id);
    setFilters({ ...INITIAL_FILTERS });
    setHiddenStatuses(new Set(DEFAULT_HIDDEN_STATUSES));
    setPersonFilter(new Set());
    setClientStatusFilterSet(new Set(INITIAL_CLIENT_STATUSES));
    setIncludePlanning(false);
    setViewNotice("Default view cleared.");
  };
  useEffect(() => {
    if (!viewNotice) return undefined;
    const t = setTimeout(() => setViewNotice(""), 3000);
    return () => clearTimeout(t);
  }, [viewNotice]);
  const [showHideMenu, setShowHideMenu] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const [groupBy, setGroupBy] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [rollForwardScope, setRollForwardScope] = useState(null);
  // The task whose completion just opened the roll-forward modal, or null.
  const [rollTask, setRollTask] = useState(null);
  // TaskType templates (T1 Personal, T2 Corporate, …) for the bulk-assign control.
  const [taskTypeOptions, setTaskTypeOptions] = useState([]);
  const [showNewTaskTypeModal, setShowNewTaskTypeModal] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [filterDraft, setFilterDraft] = useState(null);
  const [statusDraft, setStatusDraft] = useState(null);

  // Column order state for drag reordering. BASE_COLUMNS and the default
  // widths now live in ./tasks/columns.
  const [columnOrder, setColumnOrder] = useState([]);
  const [draggingCol, setDraggingCol] = useState(null);

  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc"); // 'asc' or 'desc'

  // Column widths state for manual resizing
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);

  // The exact column keys rendered for the active tab, in DOM order. Drives the
  // <colgroup> and the group-header colSpan so <col> widths line up with the
  // header/body cells (table-layout: fixed sizes columns positionally).
  const renderedCols = useMemo(() => renderedColumnKeys(activeTab), [activeTab]);

  // Which general statuses count as "Completed". A row is finished if
  // `is_completed` is set OR it sits in one of these — the same rule the
  // server uses to stamp completed_date, because this grid's Status dropdown
  // never touches the boolean.
  const completedStatusIds = useMemo(
    () =>
      new Set(
        (statuses.general || [])
          .filter((s) => (s.name || "").trim().toLowerCase() === "completed")
          .map((s) => s.id)
      ),
    [statuses]
  );
  const isFinished = (row) =>
    !!row.is_completed || completedStatusIds.has(row.status);

  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  // Initialize column order. BASE_COLUMNS is a module constant now, so it is
  // not a valid dependency.
  useEffect(() => {
    setColumnOrder((prev) => (prev.length ? prev : BASE_COLUMNS));
  }, []);

  // Column reorder helper
  const moveColumn = (fromKey, toKey) => {
    setColumnOrder((prev) => {
      const fromIdx = prev.indexOf(fromKey);
      const toIdx = prev.indexOf(toKey);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return prev;
      const next = [...prev];
      const [picked] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, picked);
      return next;
    });
  };

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  
  // Save active tab
  React.useEffect(() => {
    sessionStorage.setItem("clientsPageActiveTab", activeTab);
  }, [activeTab]);

  // Load TaskType templates once on mount for the bulk-assign dropdown.
  React.useEffect(() => {
    getTaskTypes()
      .then((list) => setTaskTypeOptions(Array.isArray(list) ? list : []))
      .catch(() => setTaskTypeOptions([]));
  }, []);

  // Helper to parse owner names from assigned_employee_names
  const getOwnerNames = (client) => {
    const v = client.assigned_employee_names;
    if (!v) return [];
    if (Array.isArray(v)) return v.filter(Boolean);
    if (typeof v === "string") {
      return v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };

  // Column resize handlers
  const handleResizeStart = (columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnKey);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnKey]);
  };

  const handleResizeMove = React.useCallback(
    (e) => {
      if (!resizingColumn) return;
      const diff = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + diff); // Min width 50px
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: newWidth,
      }));
    },
    [resizingColumn, startX, startWidth]
  );

  const handleResizeEnd = React.useCallback(() => {
    setResizingColumn(null);
  }, []);

  React.useEffect(() => {
    if (resizingColumn) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [resizingColumn, handleResizeMove, handleResizeEnd]);

  // Utility functions
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    const parts = dateString.split("T")[0].split("-");
    if (parts.length === 3) return dateString.split("T")[0];
    return dateString;
  };

  const formatDate = (dateString, format = "MMM DD") => {
    if (!dateString) return "—";
    // Parse date parts directly to avoid timezone shift
    const parts = dateString.split("T")[0].split("-");
    if (parts.length !== 3) return dateString;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // 0-indexed
    const day = parseInt(parts[2], 10);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    if (format === "MMM DD") return `${months[month]} ${day}`;
    if (format === "MMM DD, YYYY")
      return `${months[month]} ${day}, ${year}`;
    return parts.join("-");
  };

  // getCellStyle for hover effects (Monday.com style with row colors)
  const getCellStyle = (rowId, colKey, rowColor) => {
    const isHovered = hoveredCell === `${rowId}-${colKey}`;
    const isStatusField = colKey.includes("status") || colKey === "priority";
    return {
      padding: "0 12px",
      background: isHovered ? "rgba(255,255,255,0.15)" : rowColor.bg,
      transition: "background-color 0.15s ease",
      fontSize: 13,
      color: rowColor.text,
      overflow: isStatusField ? "visible" : "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      borderRight: "1px solid rgba(0,0,0,0.06)",
      position: isStatusField ? "relative" : "static",
    };
  };

  // Clickable Status Component with Portal
  const ClickableStatus = ({
    clientId,
    field,
    value,
    options,
    onChange,
    placeholder = "—",
    valueDisplay,
  }) => {
    const menuKey = `${clientId}-${field}`;
    const isOpen = openStatusMenu?.key === menuKey;
    const selectedOption =
      options.find((opt) => opt.id === value) || valueDisplay || null;
    const pillRef = useRef(null);

    const handleOpen = (e) => {
      e.stopPropagation();
      console.log("ClickableStatus opened:", {
        clientId,
        field,
        value,
        menuKey,
      });
      const rect = pillRef.current?.getBoundingClientRect();
      console.log("Pill rect:", rect);
      setOpenStatusMenu((prev) => {
        const newState =
          prev?.key === menuKey
            ? null
            : {
                key: menuKey,
                rect,
                onChange, // ✅ Store the onChange handler
                options, // ✅ Store the options
                value, // ✅ Store current value
              };
        console.log("Status menu state:", newState);
        return newState;
      });
    };

    return (
      <div style={{ position: "relative", width: "100%" }} data-status-menu>
        <div
          ref={pillRef}
          onClick={handleOpen}
          style={{
            padding: "4px 8px",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
            textAlign: "center",
            borderRadius: 4,
            background: selectedOption?.color || "rgba(255,255,255,0.2)",
            color: "#ffffff",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedOption ? selectedOption.name : placeholder}
        </div>
      </div>
    );
  };


  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest("[data-status-menu]")) {
        setOpenStatusMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate metrics
  const metricsByClient = useMemo(() => {
    // Build a map of id -> client for chain traversal
    const clientById = {};
    for (const c of clients) clientById[c.id] = c;

    // Helper: get all IDs in the rolled_forward chain for a client
    // e.g. FY2025 client -> [FY2025.id, FY2024.id, FY2023.id, ...]
    const getChainIds = (client) => {
      const ids = [client.id];
      let current = client;
      while (current.rolled_forward_from) {
        const prior = clientById[current.rolled_forward_from];
        if (!prior) break; // prior year not loaded (filtered out), stop
        ids.push(prior.id);
        current = prior;
      }
      return ids;
    };

    // First pass: compute raw metrics per client ID from invoices
    const raw = {};
    for (const c of clients) raw[c.id] = { time: 0, invoiced: 0, ar: 0 };
    for (const inv of invoices) {
      const cid = inv.client;
      if (!raw[cid]) continue;
      const total = Number(inv.total || inv.total_amount || 0);
      const paid = Number(inv.paid_amount || 0);
      raw[cid].invoiced += total;
      raw[cid].ar += total - paid;
    }

    // Second pass: for each client, sum raw metrics across its full chain
    const m = {};
    for (const c of clients) {
      const chainIds = getChainIds(c);
      m[c.id] = { time: 0, invoiced: 0, ar: 0 };
      for (const id of chainIds) {
        if (raw[id]) {
          m[c.id].invoiced += raw[id].invoiced;
          m[c.id].ar += raw[id].ar;
        }
      }
    }

    return m;
  }, [clients, invoices]);

  // Handle status change
  const handleStatusChange = async (taskId, field, value) => {
    // Find the row so we can derive task_category for side-effects.
    const row = clients.find((c) => c.id === taskId);

    // Build the payload: the requested field plus any auto-fill side-effects.
    const today = new Date().toISOString().slice(0, 10);
    const sideEffects = (() => {
      if (value == null) return {};
      const opts =
        field === "status" ? (statuses.general || []) :
        field === "gst_status" ? (statuses.tax_filing || []) :
        null;
      if (!opts) return {};
      const opt = opts.find((o) => o.id === value);
      if (!opt) return {};
      const name = (opt.name || "").toLowerCase();
      if (field === "status" && name.includes("received") && name.includes("doc")) {
        return { docs_received_date: today };
      }
      if (field === "status" && name === "filed") {
        return row?.task_category === "personal"
          ? { efile_date: today }
          : { efiled_date: today };
      }
      return {};
    })();

    // Choosing a "Completed" status finishes the file properly: the boolean
    // goes with it (the server refuses to roll forward a task that is not
    // is_completed), and once saved the roll-forward modal opens -- the same
    // offer the task page's checkmark makes.
    const completing =
      field === "status" && completedStatusIds.has(value) &&
      row && !isFinished(row);
    if (completing) sideEffects.is_completed = true;

    const payload = { [field]: value, ...sideEffects };

    // Optimistic UI update
    setClients((prev) =>
      prev.map((c) => (c.id === taskId ? { ...c, ...payload } : c))
    );

    try {
      const resp = await apiPatch(`/api/tasks/${taskId}/`, payload);
      const serverData = resp?.data ?? resp;
      if (serverData && typeof serverData === "object") {
        setClients((prev) =>
          prev.map((c) => (c.id === taskId ? { ...c, ...serverData } : c))
        );
      }
      // Opened even with no fiscal year, so the pop-up can say why it
      // cannot roll -- it used to stay silent.
      if (completing) {
        setRollTask({ ...row, ...payload,
                      ...(serverData && typeof serverData === "object" ? serverData : {}) });
      }
    } catch (error) {
      console.error("Status change failed:", error);
      setErr(`Failed to update ${field}: ${error?.message || "Unknown error"}`);
      setTimeout(() => setErr(""), 5000);

      // Revert by refetching the task
      try {
        const fresh = await apiGet(`/api/tasks/${taskId}/`);
        const freshData = fresh?.data ?? fresh;
        if (freshData) {
          setClients((prev) =>
            prev.map((c) => (c.id === taskId ? freshData : c))
          );
        }
      } catch (refetchError) {
        console.error("Refetch also failed:", refetchError);
      }
    }
  };

  // PATCH a single plain field on a task (the tax checkboxes, the priority
  // flag, the personal Type). Optimistic, then reconciled with whatever the
  // server returns — which matters for personal_type, because the server
  // stamps due_date in the same response.
  const handleFieldChange = async (taskId, field, value) => {
    const payload = { [field]: value };

    setClients((prev) =>
      prev.map((c) => (c.id === taskId ? { ...c, ...payload } : c))
    );

    try {
      const resp = await apiPatch(`/api/tasks/${taskId}/`, payload);
      const serverData = resp?.data ?? resp;
      if (serverData && typeof serverData === "object") {
        setClients((prev) =>
          prev.map((c) => (c.id === taskId ? { ...c, ...serverData } : c))
        );
      }
      // A slip check may have spawned/updated a separate T Slips task that isn't
      // in this response — reload so the T Slips tab reflects it.
      if (SLIP_FIELDS.includes(field) && value === true) {
        await fetchClients();
      }
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
      setErr(`Failed to update ${field}: ${error?.message || "Unknown error"}`);
      setTimeout(() => setErr(""), 5000);

      try {
        const fresh = await apiGet(`/api/tasks/${taskId}/`);
        const freshData = fresh?.data ?? fresh;
        if (freshData) {
          setClients((prev) =>
            prev.map((c) => (c.id === taskId ? freshData : c))
          );
        }
      } catch (refetchError) {
        console.error("Refetch also failed:", refetchError);
      }
    }
  };

  // Corporate/Personal tab slip checkbox. Checking defers to handleFieldChange
  // (backend mirrors onto the client's shared T Slips task). Unchecking asks
  // before touching that mirrored task — clearing the flag, or offering to
  // delete the T Slips task when this was its last slip and no time is logged.
  const handleSlipToggle = async (row, field, value) => {
    if (value === true) {
      handleFieldChange(row.id, field, true);
      return;
    }

    const plan = planTslipUncheck(clients, {
      clientId: row.client,
      fiscalYear: row.fiscal_year,
      field,
    });

    // Always clear the source task regardless of the answer.
    handleFieldChange(row.id, field, false);
    if (!plan) return;

    const label = SLIP_LABELS[field] || field;
    const ok =
      plan.action === "delete"
        ? window.confirm(
            `Clear ${label}? This client's T Slips task has no other slips ` +
              `flagged and no time logged — delete the T Slips task too?`
          )
        : window.confirm(`Also clear ${label} on this client's T Slips task?`);
    if (!ok) return;

    try {
      if (plan.action === "delete") {
        await apiDelete(`/api/tasks/${plan.tslipTaskId}/`);
      } else {
        await apiPatch(`/api/tasks/${plan.tslipTaskId}/`, { [field]: false });
      }
      await fetchClients();
    } catch (error) {
      console.error("Failed to update T Slips task:", error);
      setErr(`Failed to update T Slips task: ${error?.message || "Unknown error"}`);
      setTimeout(() => setErr(""), 5000);
      await fetchClients();
    }
  };

  // ----- Column renderers shared by the tab-specific column sets -----

  // A tax checkbox column header (T4 / T5 / T1135 / Compilation).
  const taxHeader = (label, columnKey) => (
    <th
      key={columnKey}
      style={{
        padding: "0 12px",
        textAlign: "center",
        fontSize: 12,
        fontWeight: 500,
        color: "#676879",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        position: "relative",
      }}
    >
      {label}
      <ResizeHandle columnKey={columnKey} onResizeStart={handleResizeStart} />
    </th>
  );

  // Docs Rec'd. Sits in its usual slot on every tab except Personal, where it
  // moves to the right of T1135.
  const docsReceivedHeader = () => (
    <th
      style={{
        padding: "0 12px",
        textAlign: "center",
        fontSize: 12,
        fontWeight: 500,
        color: "#676879",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <SortableHeader
          field="docsReceived"
          label="Docs Rec'd"
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
        <ResizeHandle
          columnKey="docsReceived"
          onResizeStart={handleResizeStart}
        />
      </div>
    </th>
  );

  const docsReceivedCell = (c, rowColor) => (
    <td
      style={{
        ...getCellStyle(c.id, "docs_received_date", rowColor),
      }}
      onMouseEnter={() => setHoveredCell(`${c.id}-docs_received_date`)}
      onMouseLeave={() => setHoveredCell(null)}
    >
      {hoveredCell === `${c.id}-docs_received_date` ? (
        <input
          type="date"
          value={formatDateForInput(c.docs_received_date || "")}
          onChange={(e) => {
            handleDateChange(c.id, "docs_received_date", e.target.value);
          }}
          onBlur={() => setHoveredCell(null)}
          onMouseLeave={(e) => {
            e.stopPropagation();
          }}
          autoFocus
          style={{
            width: "100%",
            padding: "4px 6px",
            borderRadius: 4,
            border: "1px solid rgba(255,255,255,0.5)",
            fontSize: 12,
            outline: "none",
            background: "rgba(255,255,255,0.9)",
            color: "#323338",
          }}
        />
      ) : (
        <div
          style={{
            cursor: "pointer",
            fontSize: 12,
            textAlign: "center",
          }}
          onClick={() => setHoveredCell(`${c.id}-docs_received_date`)}
        >
          {c.docs_received_date
            ? formatDate(c.docs_received_date, "MMM DD")
            : "Set"}
        </div>
      )}
    </td>
  );

  // Handle owner modal
  const handleOwnerModalOpen = (client) => {
    console.log("=== OPENING OWNER MODAL ===");
    console.log("Client:", client.name);
    console.log("Raw assigned_employees:", client.assigned_employees);
    console.log("Raw assigned_employee_names:", client.assigned_employee_names);

    setSelectedClientForOwner(client);

    // Handle different formats and ensure we always get numeric IDs
    let currentOwners = [];

    if (
      Array.isArray(client.assigned_employees) &&
      client.assigned_employees.length > 0
    ) {
      // Backend returned assigned_employees
      currentOwners = client.assigned_employees
        .map((emp) => {
          // Handle object format: {id: 1, full_name: "..."}
          if (typeof emp === "object" && emp !== null) {
            return parseInt(emp.id, 10);
          }
          // Handle direct ID: 1 or "1"
          return parseInt(emp, 10);
        })
        .filter((id) => !isNaN(id)); // Remove any invalid IDs
    } else if (client.assigned_employee_names) {
      // Fallback: Try to match names to employee IDs
      // assigned_employee_names could be array or comma-separated string
      const names = Array.isArray(client.assigned_employee_names)
        ? client.assigned_employee_names
        : client.assigned_employee_names.split(",").map((n) => n.trim());

      console.log("Parsing employee names:", names);

      // Match names to employee IDs
      currentOwners = employees
        .filter(
          (emp) => names.includes(emp.name) || names.includes(emp.full_name)
        )
        .map((emp) => parseInt(emp.id, 10));

      console.log("Matched owners from names:", currentOwners);
    }

    console.log("Normalized owner IDs:", currentOwners);
    console.log(
      "Employee IDs in list:",
      employees.map((e) => e.id)
    );

    setSelectedOwners(currentOwners);
    setShowOwnerModal(true);
  };

  // `ids`: the employee ids ticked in OwnerPickerModal.
  const handleOwnerSave = async (ids = selectedOwners) => {
    console.log("=== OWNER SAVE START ===");
    console.log("Client:", selectedClientForOwner?.name);
    console.log("Selected owner IDs:", ids);

    try {
      if (!selectedClientForOwner) {
        console.error("No client selected");
        return;
      }

      const selectedEmployeeNames = employees
        .filter((emp) => ids.includes(emp.id))
        .map((emp) => emp.name);

      console.log("Selected employee names:", selectedEmployeeNames);

      // Optimistic update
      setClients((prev) =>
        prev.map((c) =>
          c.id === selectedClientForOwner.id
            ? {
                ...c,
                assigned_employees: ids.map((id) => ({ id })), // Store as objects to match backend format
                assigned_employee_names: selectedEmployeeNames, // Array format
              }
            : c
        )
      );

      console.log("Sending to API:", { employee_ids: ids });
      const putResponse = await apiPatch(
        `/api/tasks/${selectedClientForOwner.id}/`,
        {
          employee_ids: ids,
        }
      );
      console.log("PATCH response:", putResponse);

      // Refresh from backend
      const refreshed = await apiGet(
        `/api/tasks/${selectedClientForOwner.id}/`
      );
      const clientData = refreshed?.data ?? refreshed;

      console.log("Refreshed client data:", clientData);

      if (clientData) {
        setClients((prev) =>
          prev.map((c) =>
            c.id === selectedClientForOwner.id ? { ...c, ...clientData } : c
          )
        );
      }

      setShowOwnerModal(false);
      setSelectedClientForOwner(null);
      setSelectedOwners([]);

      console.log("=== OWNER SAVE SUCCESS ===");
    } catch (error) {
      console.error("=== OWNER SAVE ERROR ===", error);
      console.error("Error response:", error?.response?.data);
      setErr(`Failed to update owner: ${error?.message || "Unknown error"}`);
      setTimeout(() => setErr(""), 5000);
    }
  };

  // Handle inline group edit save. NOTE: `taskId` is a Task row id, but
  // group_name lives on the Client — look up the real client id from the row.
  const handleGroupSave = async (taskId) => {
    if (groupSaveInFlightRef.current) return;
    groupSaveInFlightRef.current = true;
    try {
      const trimmed = editingGroupValue.trim();
      const row = clients.find((c) => c.id === taskId);
      const currentGroup = row?.group_name || "";
      if (trimmed === currentGroup) {
        return;
      }
      const realClientId = row?.client;
      if (!realClientId) {
        return;
      }
      try {
        const groupId = await resolveClientGroup(trimmed);
        await apiPatch(`/api/clients/${realClientId}/`, { group: groupId });
        setClients((prev) =>
          prev.map((c) =>
            c.id === taskId
              ? { ...c, group: groupId, group_name: trimmed || null }
              : c
          )
        );
      } catch (error) {
        console.error("Error updating group:", error);
        setErr(`Failed to update group: ${error.message}`);
        setTimeout(() => setErr(""), 5000);
      }
    } finally {
      setEditingGroupClientId(null);
      groupSaveInFlightRef.current = false;
    }
  };

  // Handle date changes on Task fields (due_date, docs_received_date, efile_date, etc.).
  const handleDateChange = async (taskId, field, value) => {
    try {
      await apiPatch(`/api/tasks/${taskId}/`, { [field]: value });
      setClients((prev) =>
        prev.map((c) => (c.id === taskId ? { ...c, [field]: value } : c))
      );
    } catch (error) {
      console.error("Error updating date:", error);
      setErr(`Failed to update date: ${error.message}`);
    }
  };

  // Handle year end change
  const handleYearEndChange = async (taskId, isCorporate, value) => {
    try {
      let yearEndDate = value;
      if (!isCorporate) {
        const currentYear = new Date().getFullYear();
        yearEndDate = `${currentYear}-12-31`;
      }
      const payload = { year_end_date: yearEndDate };
      if (yearEndDate) {
        const d = new Date(yearEndDate + 'T00:00:00');
        if (!Number.isNaN(d.getTime())) {
          payload.fiscal_year = d.getFullYear();
        }
      }
      await apiPatch(`/api/tasks/${taskId}/`, payload);
      setClients((prev) =>
        prev.map((c) =>
          c.id === taskId ? { ...c, year_end_date: yearEndDate, fiscal_year: payload.fiscal_year ?? c.fiscal_year } : c
        )
      );
    } catch (error) {
      console.error("Error updating year end:", error);
      setErr(`Failed to update year end: ${error.message}`);
    }
  };

  // File handlers
  // NOTE: after the I2 retarget, rows in `clients` state are Task rows.
  // ClientFile lookups need the parent client id (row.client), not the
  // task id (row.id). Falls back to row.id only if `client` is missing
  // (legacy Client row shape, defensive).
  const handleFileModalOpen = async (row) => {
    const clientId = row.client ?? row.id;
    try {
      const freshClient = await apiGet(`/api/clients/${clientId}/`);
      setSelectedClientForFiles(freshClient.data || freshClient);
      setFileModalOpen(true);
    } catch (error) {
      setSelectedClientForFiles(row);
      setFileModalOpen(true);
    }
  };

  const handleSharePointPickerOpen = async (row) => {
    // Always fetch fresh client data so selectedClientForFiles.id is
    // guaranteed to be present, even if the in-memory object is stale.
    const clientId = row.client ?? row.id;
    try {
      const freshClient = await apiGet(`/api/clients/${clientId}/`);
      setSelectedClientForFiles(freshClient.data || freshClient);
    } catch {
      // Fall back to the row object if the fetch fails
      setSelectedClientForFiles(row);
    }
    setSharePointPickerOpen(true);
  };

  const handleSharePointFileSelected = async (fileMetadata) => {
    if (!selectedClientForFiles?.id) {
      alert("No client selected — please close and try again.");
      return;
    }
    try {
      await apiPost(`/api/sharepoint/files/attach/`, {
        client_id: selectedClientForFiles.id,
        file_metadata: fileMetadata,
      });
      await fetchClients();
      setSharePointPickerOpen(false);
      setSelectedClientForFiles(null);
    } catch (error) {
      console.error("Error attaching file:", error);
      alert("Failed to attach file.");
    }
  };

  // After a file is added/removed, refetch the whole list. Files are
  // client-level, and the row in `clients` is a Task — the embedded
  // file_count / files come from the parent client, so any task with the
  // same client should pick up the change. Easiest is to refetch.
  const handleFileAdded = async () => {
    try { await fetchClients(); }
    catch (error) { console.error("Error refreshing list:", error); }
  };

  const handleFileRemoved = async (fileId, newFileList) => {
    try { await fetchClients(); }
    catch (error) { console.error("Error refreshing list:", error); }
  };

  // Delete task handler.
  //
  // Rows on this grid are Tasks (fetched from /api/tasks/), not Clients. This
  // previously called DELETE /api/clients/<id>/ with a Task id, which deleted
  // whichever unrelated Client happened to share that id and left the task
  // behind. It must hit /api/tasks/.
  const handleDeleteTask = async (task) => {
    const label = taskLabel(task);
    const confirmMessage = `Are you sure you want to delete the task "${label}"?\n\nThis action cannot be undone. The client is not deleted.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteTask(task.id);

      setClients((prev) => prev.filter((c) => c.id !== task.id));

      // Close any open panels for this row
      if (selectedClientForUpdates?.id === task.id) {
        setShowUpdatesPanel(false);
        setSelectedClientForUpdates(null);
      }
      if (selectedClientForFiles?.id === task.id) {
        setFileModalOpen(false);
        setSelectedClientForFiles(null);
      }
    } catch (error) {
      console.error("Error deleting task:", error);

      let errorMessage = "Failed to delete task";

      let errorData = error.response?.data;
      if (!errorData && error.message) {
        try {
          errorData = JSON.parse(error.message);
        } catch (e) {
          errorData = { message: error.message };
        }
      }

      if (errorData) {
        errorMessage = errorData.detail || errorData.message || error.message;
      }

      alert(`Failed to delete task:\n\n${errorMessage}`);
    }
  };

  // Updates handler
  const handlePostUpdate = async (clientId, text) => {
    try {
      // Try to call backend API to persist update
      try {
        await apiPost(`/api/client-updates/`, {
          client: clientId,
          content: text,
          mentioned_user_ids: extractMentions(text, employees),
        });

        // Refresh the full client data to get the saved update from backend
        const [refreshedClient, updates] = await Promise.all([
          apiGet(`/api/clients/${clientId}/`),
          apiGet(`/api/client-updates/by_client/${clientId}/`),
        ]);

        const clientData = {
          ...(refreshedClient.data || refreshedClient),
          updates: updates,
        };

        setClients((prev) =>
          prev.map((c) => (c.id === clientId ? clientData : c))
        );

        if (selectedClientForUpdates?.id === clientId) {
          setSelectedClientForUpdates(clientData);
        }
      } catch (apiError) {
        // If API endpoint doesn't exist yet, fall back to local state
        console.log("Updates API not available, using local state:", apiError);

        const newUpdate = {
          id: Date.now(),
          content: text,
          text: text,
          user_name: user?.name || user?.full_name || "You",
          author: user?.name || user?.full_name || "You",
          created_at: new Date().toISOString(),
        };

        setClients((prev) =>
          prev.map((c) =>
            c.id === clientId
              ? { ...c, updates: [newUpdate, ...(c.updates || [])] }
              : c
          )
        );

        if (selectedClientForUpdates?.id === clientId) {
          setSelectedClientForUpdates((prev) => ({
            ...prev,
            updates: [newUpdate, ...(prev.updates || [])],
          }));
        }
      }
    } catch (error) {
      console.error("Error posting update:", error);
      alert("Failed to post update. Please try again.");
    }
  };

  // Create new client
  const handleCreateClient = async (formData) => {
    console.log("=== CLIENT CREATE START ===");
    console.log("Form data:", formData);
    try {
      // Prepare the payload - only send defined fields
      const payload = {
        name: formData.name,
        is_corporate: formData.is_corporate,
      };

      // Optional fields - only include if they have values
      if (formData.group && formData.group.trim()) {
        payload.group = await resolveClientGroup(formData.group);
      }

      if (formData.owner && formData.owner.length > 0) {
        payload.employee_ids = formData.owner;
      }

      // Only include status/priority if they are set
      if (formData.status) {
        payload.status = formData.status;
      }

      if (formData.priority) {
        payload.priority = formData.priority;
      }

      // Include fiscal_year if provided (parse as integer)
      if (formData.fiscal_year) {
        payload.fiscal_year = parseInt(formData.fiscal_year, 10);
      }

      // Include client_status if provided
      if (formData.client_status) {
        payload.client_status = formData.client_status;
      }

      // Only include dates if they have values and are in YYYY-MM-DD format
      if (formData.year_end_date && formData.year_end_date.trim()) {
        payload.year_end_date = formData.year_end_date.trim();
      }

      if (formData.due_date && formData.due_date.trim()) {
        payload.due_date = formData.due_date.trim();
      }

      console.log("Sending to API:", payload);
      const response = await apiPost("/api/clients/", payload);
      console.log("POST response:", response);

      // Refresh client data - response is the client object directly
      const clientId = response.id;
      const refreshed = await apiGet(`/api/clients/${clientId}/`);
      console.log("Refreshed client data:", refreshed);

      setClients((prev) => [refreshed, ...prev]);
      setShowNewClientModal(false);
      console.log("=== CLIENT CREATE SUCCESS ===");

      // The client exists now whatever happens next. addMasterFile never
      // throws, so a SharePoint failure lands here, not in the "Failed to
      // create client" handler below.
      if (formData.add_master_file) {
        const result = await addMasterFile(clientId);
        alert(
          result.ok
            ? `${refreshed?.name || "Client"} created. ${result.message}`
            : `${refreshed?.name || "Client"} was created, but the master file was not:\n\n${result.message}\n\nYou can try again from the client's Files tab.`
        );
      }
    } catch (error) {
      console.error("Error creating client:", error);
      console.error("Error response:", error.response?.data);

      // Better error handling for different error types
      let errorMessage = "Failed to create client";

      // Try to get error data from response or parse from message
      let errorData = error.response?.data;
      if (!errorData && error.message) {
        try {
          errorData = JSON.parse(error.message);
        } catch (e) {
          // If not JSON, use the message as is
          errorData = { message: error.message };
        }
      }

      if (errorData) {
        // Handle name uniqueness errors
        if (errorData.name) {
          const nameError = Array.isArray(errorData.name)
            ? errorData.name[0]
            : errorData.name;
          errorMessage = `Client Name Error: ${nameError}\n\nPlease:\n• Use a different name\n• Check if this client already exists\n• Check inactive clients if you recently archived one with this name`;
        }
        // Handle other field errors
        else if (typeof errorData === "object" && !errorData.detail) {
          const fieldErrors = Object.entries(errorData)
            .map(([field, errors]) => {
              const errorText = Array.isArray(errors) ? errors[0] : errors;
              return `${field}: ${errorText}`;
            })
            .join("\n");
          errorMessage = `Validation errors:\n${fieldErrors}`;
        }
        // Handle general error messages
        else {
          errorMessage = errorData.detail || errorData.message || error.message;
        }
      }

      alert(`Failed to create client:\n\n${errorMessage}`);
    }
  };

  // Client Status change handler
  const handleClientStatusChange = async (clientId, newStatus) => {
    try {
      await apiPut(`/api/clients/${clientId}/`, { client_status: newStatus });
      setClients((prev) =>
        prev.map((c) =>
          c.id === clientId ? { ...c, client_status: newStatus } : c
        )
      );
    } catch (error) {
      console.error("Error updating client status:", error);
      alert("Failed to update client status");
    }
  };

  // Stop a file rolling forward, or let it roll again (NextYearCell). Throws
  // so the cell can show the error and stay open.
  const handleRollStop = async (row, patch) => {
    let saved;
    try {
      saved = await updateTask(row.id, patch);
    } catch (e) {
      throw new Error(apiErrorMessage(e, "Could not save that."));
    }
    const [fresh] = normalizeRows([saved]);
    setClients((prev) => prev.map((c) => (c.id === row.id ? { ...c, ...fresh } : c)));
  };

  // Check if client needs roll-forward
  const needsRollForward = (client) => {
    if (!client.year_end_date || !client.fiscal_year) return false;
    const months = monthsUntil(client.year_end_date);
    return months !== null && months >= 0 && months <= 2;
  };

  // Get client status colors
  const getClientStatusColor = (status) => {
    const colors = {
      active: { bg: "#00c875", text: "#ffffff" },
      inactive: { bg: "#c4c4c4", text: "#ffffff" },
      completed: { bg: "#0073ea", text: "#ffffff" },
    };
    return colors[status] || colors.active;
  };

  // Format client status for display
  const formatClientStatus = (status) => {
    const labels = {
      active: "Active",
      inactive: "Inactive",
      completed: "Completed",
    };
    return labels[status] || "Active";
  };

  // Rows that pass every filter EXCEPT the activeTab. Logic lives in
  // ./tasks/taskFilters so it can be read and tested on its own.
  const preTabFiltered = filterRowsPreTab(clients, {
    query,
    filters,
    personFilter,
    clientStatusFilterSet,
    isFinished,
    hiddenStatuses,
  });

  const filteredClients = filterRowsByTab(preTabFiltered, activeTab);

  const sortedClients = sortRows(
    filteredClients,
    sortField,
    sortDirection,
    metricsByClient
  );

  // Apply Group By: produce a flat array interleaving group-header markers
  // with the client rows that belong to each group. When groupBy is "" we just
  // pass the sorted clients through unchanged.
  const getGroupValue = (c, key) => {
    switch (key) {
      case "group":
        return c.group_name || "(No group)";
      case "owner": {
        const names = c.assigned_employee_names;
        if (Array.isArray(names) && names.length) return names.join(", ");
        if (typeof names === "string" && names.trim()) return names;
        return "(Unassigned)";
      }
      case "status": {
        const sId = c.status;
        const s = (statuses.general || []).find((x) => x.id === sId);
        return s ? s.name : "(No status)";
      }
      case "type":
        return isCorporate(c) ? "Corporate" : "Personal";
      case "yearEndMonth": {
        if (!c.year_end_date) return "(No year-end)";
        // From the string, not new Date(): that is UTC midnight, which puts
        // a first-of-the-month year end in the previous month here.
        const m = yearEndMonthOf(c);
        return (m && MONTHS[m - 1]) || "(Unknown)";
      }
      default:
        return "";
    }
  };
  const groupedRows = useMemo(() => {
    if (!groupBy) {
      return sortedClients.map((c) => ({ kind: "row", client: c }));
    }
    const buckets = new Map();
    for (const c of sortedClients) {
      const key = String(getGroupValue(c, groupBy));
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(c);
    }
    const result = [];
    for (const [label, rows] of buckets) {
      result.push({ kind: "header", label, count: rows.length });
      const collapsed = collapsedGroups.has(label);
      if (!collapsed) {
        for (const c of rows) result.push({ kind: "row", client: c });
      }
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getGroupValue only reads `statuses`, which is already listed above; isCorporate is a stable module import.
  }, [sortedClients, groupBy, collapsedGroups, statuses]);

  // 1-based ordinal per rendered row (headers get null). Precomputed so the
  // windowed render can label any row without a running counter.
  const rowOrdinals = useMemo(() => {
    const out = new Array(groupedRows.length);
    let n = 0;
    for (let i = 0; i < groupedRows.length; i++) {
      if (groupedRows[i].kind === "row") { n += 1; out[i] = n; }
      else out[i] = null;
    }
    return out;
  }, [groupedRows]);
  const toggleGroup = (label) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Export functions
  const exportToCSV = () => {
    const headers = [
      "Name",
      "Group",
      "Type",
      "Owner",
      "Status",
      "Flagged",
      "Year End",
      "Due Date",
      "Completed",
      "Docs Rec'd",
      "Efile Number",
      "Efile Date",
      "P-GST",
      "T1135",
      "CDA",
      "T4",
      "T5",
      "GST",
      "Cap. Div.",
      "CRA Auth",
      "Time",
      "Invoiced",
      "A/R",
      "Net",
      "Files",
      "Active",
    ];
    const rows = sortedClients.map((c) => {
      const m = metricsByClient[c.id] || { time: 0, invoiced: 0, ar: 0 };

      // Handle assigned_employee_names as array or string
      let ownerNames = "";
      if (Array.isArray(c.assigned_employee_names)) {
        ownerNames = c.assigned_employee_names.join(", ");
      } else if (typeof c.assigned_employee_names === "string") {
        ownerNames = c.assigned_employee_names;
      }

      return [
        c.name || "",
        c.group_name || "",
        isCorporate(c) ? "Corporate" : "Personal",
        ownerNames,
        c.status || "",
        c.is_flagged ? "Yes" : "",
        c.year_end_date || "",
        c.due_date || "",
        c.completed_date || "",
        c.docs_received_date || "",
        c.efile_number || "",
        c.efile_date || "",
        c.efiled_date || "",
        c.gst_status || "",
        c.t1135_status || "",
        c.cda_status || "",
        c.t4_status || "",
        c.t5_status || "",
        c.gst_status || "",
        c.cap_div_status || "",
        c.cra_auth_lhcpa_status || "",
        c.cra_auth_personal || "",
        m.time.toFixed(2),
        m.invoiced.toFixed(2),
        m.ar.toFixed(2),
        (m.time - m.invoiced).toFixed(2),
        c.file_count || 0,
        c.is_active ? "Yes" : "No",
      ];
    });
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `clients_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    setShowExportMenu(false);
  };

  const exportToPDF = async () => {
    try {
      const printWindow = window.open("", "_blank");
      const html = `
        <!DOCTYPE html>
        <html><head><title>Tasks Report</title>
        <style>body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border:1px solid #ddd;padding:8px;text-align:left;font-size:12px}th{background-color:#f6f7fb;font-weight:600}</style>
        </head><body><h1>Clients Report - ${new Date().toLocaleDateString()}</h1><table><thead><tr>
        <th>Name</th><th>Group</th><th>Type</th><th>Owner</th><th>Status</th><th>Time</th><th>Invoiced</th><th>A/R</th><th>Net</th>
        </tr></thead><tbody>${sortedClients
          .map((c) => {
            const m = metricsByClient[c.id] || { time: 0, invoiced: 0, ar: 0 };
            return `<tr><td>${c.name}</td><td>${c.group_name || "—"}</td><td>${
              isCorporate(c) ? "Corp" : "Personal"
            }</td>
          <td>${c.assigned_employee_names || "—"}</td>
          <td>${c.status || "—"}</td><td>$${m.time.toFixed(
              2
            )}</td><td>$${m.invoiced.toFixed(2)}</td>
          <td>$${m.ar.toFixed(2)}</td><td>$${(m.time - m.invoiced).toFixed(
              2
            )}</td></tr>`;
          })
          .join("")}</tbody></table></body></html>`;
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
      setShowExportMenu(false);
    } catch (error) {
      alert("Failed to export to PDF.");
    }
  };

  const uniqueGroups = [
    ...new Set(clients.map((c) => c.group_name).filter(Boolean)),
  ].sort();
  // Years actually present in the data, plus the default -- so the dropdown
  // never offers an empty year, and always offers the one the grid opens on
  // even before any task has been stamped with it.
  // Which year "Roll all Personal" rolls from: the toolbar's Fiscal year,
  // or last year on "All years" (personal returns are filed the spring after).
  const personalRollYear = filters.fiscalYear
    ? parseInt(filters.fiscalYear, 10)
    : Number(CURRENT_FISCAL_YEAR) - 1;
  const uniqueFiscalYears = [
    ...new Set([
      ...clients.map((c) => c.fiscal_year).filter((y) => y != null).map(String),
      CURRENT_FISCAL_YEAR,
    ]),
  ].sort((a, b) => Number(b) - Number(a));
  // Clears the Filter panel only. Fiscal year and year-end month live in the
  // toolbar now and keep their values -- they are the grid's baseline view
  // rather than a filter switched on in the panel.
  const clearFilters = () =>
    setFilters((prev) => ({
      group: "",
      owner: "",
      priority: "",
      type: "",
      fiscalYear: prev.fiscalYear,
      yearEndMonth: prev.yearEndMonth,
      t4_status: "",
      t5_status: "",
      gst_status: "",
      cap_div_status: "",
      cra_auth_status: "",
      efile_number: "",
      efile_date: "",
      personal_gst_status: "",
      t1135_status: "",
      cda_status: "",
      cra_auth_personal: "",
    }));
  // The Filter panel's own indicator: the toolbar's year and month don't count.
  const hasActiveFilters = Object.entries(filters).some(
    ([k, v]) => v !== "" && k !== "fiscalYear" && k !== "yearEndMonth");

  const getRowStatusColor = (client) => {
    if (client.status) {
      const code = client.status.code || "default";
      return MONDAY_COLORS[code] || MONDAY_COLORS.default;
    }
    return MONDAY_COLORS.default;
  };

  // Bulk-selection helpers
  const toggleRowSelected = (id) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleSelectAll = (rows) => {
    setSelectedRowIds((prev) => {
      const allIds = rows.map((r) => String(r.id));
      const allSelected = allIds.length > 0 && allIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        for (const id of allIds) next.delete(id);
      } else {
        for (const id of allIds) next.add(id);
      }
      return next;
    });
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          backgroundColor: "#f6f7fb",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoadingSpinner text="Loading tasks..." />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        // App.js gives this route a 64px top margin for the TopBar; subtract
        // that so the table-area's horizontal scrollbar lands inside the
        // viewport instead of below the fold.
        height: "calc(100vh - 64px)",
        backgroundColor: "#f6f7fb",
        fontFamily: "Figtree, -apple-system, sans-serif",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <RollForwardNudge user={user} />

        {/* Header */}
        <div
          style={{
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #d0d4e4",
            padding: "10px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 52,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 400,
                color: "#323338",
              }}
            >
              Tasks
            </h1>

            <div style={{ display: "flex", gap: 4 }}>
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: "5px 14px",
                    fontSize: 13,
                    fontWeight: 400,
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                    backgroundColor:
                      activeTab === tab.key ? "#e6f4ff" : "transparent",
                    color: activeTab === tab.key ? "#0073ea" : "#676879",
                  }}
                >
                  {tab.label} (
                  {OTHER_KINDS.has(tab.key)
                    ? otherTasks.filter((t) =>
                        isBookkeepingTask(t, otherTaskTypes) ===
                          (tab.key === "bookkeeping")).length
                    : filterRowsByTab(preTabFiltered, tab.key).length}
                  )
                </button>
              ))}
            </div>


            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                style={{
                  padding: "5px 12px",
                  fontSize: 13,
                  border: "1px solid #d0d4e4",
                  borderRadius: 4,
                  backgroundColor: "#ffffff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#323338",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export
              </button>

              {showExportMenu && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    right: 0,
                    backgroundColor: "#ffffff",
                    border: "1px solid #d0d4e4",
                    borderRadius: 8,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    overflow: "hidden",
                    zIndex: 100,
                    minWidth: 140,
                  }}
                >
                  <button
                    onClick={exportToCSV}
                    style={{
                      width: "100%",
                      padding: "10px 16px",
                      fontSize: 13,
                      border: "none",
                      background: "none",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f6f7fb")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "transparent")
                    }
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={exportToPDF}
                    style={{
                      width: "100%",
                      padding: "10px 16px",
                      fontSize: 13,
                      border: "none",
                      background: "none",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f6f7fb")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "transparent")
                    }
                  >
                    Export as PDF
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowCreateTaskModal(true)}
              style={{
                padding: "5px 14px",
                fontSize: 13,
                border: "none",
                borderRadius: 4,
                backgroundColor: "#0073ea",
                color: "#ffffff",
                cursor: "pointer",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New Task
            </button>
          </div>
        </div>

        {/* New toolbar — replaces the old radio-bar */}
        <div
          style={{
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #e5e7eb",
            padding: "8px 24px",
            display: "flex",
            alignItems: "center",
            gap: 16,
            position: "relative",
            zIndex: 50,
          }}
        >
          {/* Search */}
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14, color: "#676879" }}>🔍</span>
            <input
              type="text"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                padding: "5px 10px",
                fontSize: 13,
                border: "1px solid transparent",
                borderRadius: 4,
                outline: "none",
                width: 160,
                background: "transparent",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#d0d4e4";
                e.target.style.background = "#ffffff";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "transparent";
                e.target.style.background = "transparent";
              }}
            />
          </div>

          {/* Status / Fiscal year / Year-end month */}
          <TaskFilterBar
            statuses={statuses.general || []}
            hiddenStatuses={hiddenStatuses}
            onHiddenStatusesChange={setHiddenStatuses}
            fiscalYear={filters.fiscalYear}
            fiscalYears={uniqueFiscalYears}
            onFiscalYearChange={(v) =>
              setFilters((f) => ({ ...f, fiscalYear: v }))}
            yearEndMonth={filters.yearEndMonth}
            onYearEndMonthChange={(v) =>
              setFilters((f) => ({ ...f, yearEndMonth: v }))}
            nextYear={filters.nextYear}
            onNextYearChange={(v) => {
              setFilters((f) => ({ ...f, nextYear: v }));
              // Every Next year state is about FINISHED files, and "won't
              // roll" is mostly inactive clients -- both hidden by default.
              // Show them, visibly in their own filters, rather than return
              // an empty grid.
              if (v) {
                setHiddenStatuses((prev) => {
                  const next = new Set(prev);
                  next.delete("completed");
                  return next;
                });
                setClientStatusFilterSet((prev) => new Set([...prev, "inactive"]));
              }
            }}
          />

          {/* Person */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => {
                setShowPersonMenu((s) => !s);
                setShowHideMenu(false);
                setShowGroupMenu(false);
              }}
              style={{
                padding: "5px 10px",
                fontSize: 13,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: personFilter.size > 0 ? "#0073ea" : "#323338",
                fontWeight: personFilter.size > 0 ? 500 : 400,
              }}
              title="Filter by assignee"
            >
              <span>👥</span>
              Person
              {personFilter.size > 0 && (
                <span
                  style={{
                    background: "#0073ea",
                    color: "white",
                    borderRadius: 10,
                    padding: "0 6px",
                    fontSize: 11,
                  }}
                >
                  {personFilter.size}
                </span>
              )}
            </button>
            {showPersonMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  background: "white",
                  border: "1px solid #d0d4e4",
                  borderRadius: 6,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  minWidth: 220,
                  maxHeight: 320,
                  overflowY: "auto",
                  zIndex: 200,
                  padding: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "4px 6px 8px",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <span style={{ fontSize: 12, color: "#676879", fontWeight: 600 }}>
                    Filter by assignee
                  </span>
                  <button
                    onClick={() => setPersonFilter(new Set())}
                    style={{
                      fontSize: 11,
                      color: "#0073ea",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                </div>
                {(employees || [])
                  .filter((emp) => emp.is_active !== false)
                  .map((emp) => {
                    const id = String(emp.id);
                    const checked = personFilter.has(id);
                    return (
                      <label
                        key={emp.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 6px",
                          cursor: "pointer",
                          fontSize: 13,
                          borderRadius: 4,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f6f7fb")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setPersonFilter((prev) => {
                              const next = new Set(prev);
                              if (checked) next.delete(id);
                              else next.add(id);
                              return next;
                            });
                          }}
                        />
                        {emp.name || emp.full_name || emp.user?.username}
                      </label>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Filter */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => {
                setShowFilters((s) => {
                  const opening = !s;
                  if (opening) {
                    setFilterDraft({ ...filters });
                    setStatusDraft(new Set(clientStatusFilterSet));
                  }
                  return opening;
                });
                setShowPersonMenu(false);
                setShowHideMenu(false);
                setShowGroupMenu(false);
              }}
              style={{
                padding: "5px 10px",
                fontSize: 13,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: showFilters ? "#0073ea" : "#323338",
              }}
            >
              <span>▽</span>
              Filter
            </button>
          </div>

          {/* Hide */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => {
                setShowHideMenu((s) => !s);
                setShowPersonMenu(false);
                setShowGroupMenu(false);
              }}
              title="Coming soon — column hiding requires a render refactor"
              style={{
                padding: "5px 10px",
                fontSize: 13,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: "#9ca3af",
                opacity: 0.6,
              }}
            >
              <span>◫</span>
              Hide
              {hiddenColumns.size > 0 && (
                <span
                  style={{
                    background: "#0073ea",
                    color: "white",
                    borderRadius: 10,
                    padding: "0 6px",
                    fontSize: 11,
                  }}
                >
                  {hiddenColumns.size}
                </span>
              )}
            </button>
            {showHideMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  background: "white",
                  border: "1px solid #d0d4e4",
                  borderRadius: 6,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  minWidth: 200,
                  maxHeight: 320,
                  overflowY: "auto",
                  zIndex: 200,
                  padding: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: "#676879",
                    fontWeight: 600,
                    padding: "4px 6px 8px",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  Hide columns
                </div>
                {BASE_COLUMNS.filter(
                  (c) => c !== "rowNum" && c !== "checkbox" && c !== "name"
                ).map((colKey) => {
                  const labels = {
                    task: "Task",
                    group: "Group",
                    files: "Files",
                    owner: "Owner",
                    status: "Status",
                    yearEnd: "Year End",
                    dueDate: "Due Date",
                    completed: "Completed",
                    nextYear: "Next Year",
                    docsReceived: "Docs Received",
                    priority: "Priority",
                    type: "Type",
                    tax1: "Tax 1",
                    tax2: "Tax 2",
                    tax3: "Tax 3",
                    tax4: "Tax 4",
                    tax5: "Tax 5",
                    tax6: "Tax 6",
                    time: "Time",
                    invoiced: "Invoiced",
                    ar: "A/R",
                  };
                  const isHidden = hiddenColumns.has(colKey);
                  return (
                    <label
                      key={colKey}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 6px",
                        cursor: "pointer",
                        fontSize: 13,
                        borderRadius: 4,
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f6f7fb")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={!isHidden}
                        onChange={() => {
                          setHiddenColumns((prev) => {
                            const next = new Set(prev);
                            if (isHidden) next.delete(colKey);
                            else next.add(colKey);
                            return next;
                          });
                        }}
                      />
                      {labels[colKey] || colKey}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Group by */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => {
                setShowGroupMenu((s) => !s);
                setShowPersonMenu(false);
                setShowHideMenu(false);
              }}
              style={{
                padding: "5px 10px",
                fontSize: 13,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: groupBy ? "#0073ea" : "#323338",
              }}
            >
              <span>≡</span>
              Group by
              {groupBy && (
                <span style={{ fontSize: 11, color: "#0073ea" }}>: {groupBy}</span>
              )}
            </button>
            {showGroupMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  background: "white",
                  border: "1px solid #d0d4e4",
                  borderRadius: 6,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  minWidth: 180,
                  zIndex: 200,
                  padding: 4,
                }}
              >
                {[
                  { key: "", label: "None" },
                  { key: "group", label: "Group" },
                  { key: "owner", label: "Owner" },
                  { key: "status", label: "Status" },
                  { key: "type", label: "Type" },
                  { key: "yearEndMonth", label: "Year-end month" },
                ].map((opt) => (
                  <button
                    key={opt.key || "none"}
                    onClick={() => {
                      setGroupBy(opt.key);
                      setShowGroupMenu(false);
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "6px 10px",
                      textAlign: "left",
                      background: groupBy === opt.key ? "#f0f7ff" : "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 13,
                      borderRadius: 4,
                      color: groupBy === opt.key ? "#0073ea" : "#323338",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background =
                        groupBy === opt.key ? "#e3f2fd" : "#f6f7fb")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        groupBy === opt.key ? "#f0f7ff" : "transparent")
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Include planning — off by default so the daily grid stays the
              day's work. The server, not the client, decides what "planning"
              widens the horizon to include, via horizon=all. */}
          <button
            onClick={() => setIncludePlanning((v) => !v)}
            title="Show work planned for future periods that nobody has started yet"
            aria-pressed={includePlanning}
            style={{
              padding: "5px 10px",
              fontSize: 13,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: includePlanning ? "#0073ea" : "#323338",
              fontWeight: includePlanning ? 500 : 400,
            }}
          >
            <span>🗓️</span>
            Include planning
          </button>

          {/* How the page opens: this tab + every filter above, per person. */}
          <button
            type="button"
            onClick={saveAsDefaultView}
            title="Open Tasks on this tab with these filters every time"
            style={{
              padding: "5px 10px", fontSize: 13, border: "none",
              background: "transparent", cursor: "pointer", color: "#323338",
            }}
          >
            ★ Save as my default view
          </button>
          <button
            type="button"
            onClick={resetDefaultView}
            title="Forget my default view and go back to the standard filters"
            style={{
              padding: "5px 10px", fontSize: 13, border: "none",
              background: "transparent", cursor: "pointer", color: "#676879",
            }}
          >
            Reset
          </button>
          {viewNotice && (
            <span role="status" style={{ fontSize: 12, color: "#00854d" }}>
              {viewNotice}
            </span>
          )}

          {/* Spacer pushes Roll-forward to the right */}
          <div style={{ flex: 1 }} />

          {/* Selection summary */}
          {selectedRowIds.size > 0 && (
            <span style={{ fontSize: 12, color: "#676879" }}>
              {selectedRowIds.size} selected
            </span>
          )}

          {/* Roll-forward (manager only, requires selection) — routes through
              the roll-forward engine's preview/tick/undo flow. selectedRowIds
              holds Task ids (this grid is task-per-row; see
              useTasksData/fetchClients, which loads from /api/tasks/), so it
              maps straight onto the engine's `task_ids` scope with no
              translation needed. */}
          {["MANAGER", "ADMIN"].includes(String(user?.role).toUpperCase()) &&
            selectedRowIds.size > 0 &&
            !OTHER_KINDS.has(activeTab) && (
              <button
                onClick={() =>
                  setRollForwardScope({
                    kind: "selection",
                    task_ids: Array.from(selectedRowIds),
                  })
                }
                style={{
                  padding: "5px 12px",
                  fontSize: 13,
                  border: "1px solid #d0d4e4",
                  borderRadius: 4,
                  backgroundColor: "#ffffff",
                  color: "#323338",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Roll forward
              </button>
            )}

          {/* Every personal task for one year, at once. Deceased (final)
              returns are skipped by the engine; the preview lists every task
              with a tick box and an option to archive the finished ones. The
              year is the toolbar's Fiscal year, or last year on "All years"
              -- personal returns are filed the spring after their year. */}
          {["MANAGER", "ADMIN"].includes(String(user?.role).toUpperCase()) &&
            activeTab === "personal" && (
              <button
                onClick={() =>
                  setRollForwardScope({
                    kind: "firm",
                    categories: ["personal"],
                    fiscal_year: personalRollYear,
                  })
                }
                style={{
                  padding: "5px 12px",
                  fontSize: 13,
                  border: "1px solid #0073ea",
                  borderRadius: 4,
                  backgroundColor: "#ffffff",
                  color: "#0073ea",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Roll all Personal {personalRollYear} → {personalRollYear + 1}
              </button>
            )}

          {/* Bulk-assign TaskType (Corporate / Personal tabs only, requires selection) */}
          {selectedRowIds.size > 0 && !OTHER_KINDS.has(activeTab) && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <select
                value=""
                disabled={bulkAssigning}
                onChange={async (e) => {
                  const raw = e.target.value;
                  if (!raw) return;
                  const taskTypeId = parseInt(raw, 10);
                  const ids = Array.from(selectedRowIds);
                  setBulkAssigning(true);
                  try {
                    await bulkAssignTaskType(ids, taskTypeId);
                    await fetchClients();
                    setSelectedRowIds(new Set());
                  } catch (err) {
                    console.error("Bulk assign failed", err);
                  } finally {
                    setBulkAssigning(false);
                    e.target.value = "";
                  }
                }}
                style={{
                  padding: "5px 8px",
                  fontSize: 13,
                  border: "1px solid #d0d4e4",
                  borderRadius: 4,
                  backgroundColor: bulkAssigning ? "#f6f7fb" : "#ffffff",
                  color: bulkAssigning ? "#9ca3af" : "#323338",
                  cursor: bulkAssigning ? "not-allowed" : "pointer",
                  fontWeight: 500,
                }}
              >
                <option value="">
                  {bulkAssigning
                    ? "Assigning…"
                    : `Assign Task (${selectedRowIds.size})…`}
                </option>
                {taskTypeOptions
                  .filter((tt) => tt.is_active !== false)
                  .map((tt) => (
                    <option key={tt.id} value={tt.id}>
                      {tt.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewTaskTypeModal(true)}
                title="Create a new Task Type"
                style={{
                  padding: "5px 10px",
                  fontSize: 13,
                  border: "1px solid #d0d4e4",
                  borderRadius: 4,
                  backgroundColor: "#ffffff",
                  color: "#323338",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                + New Task
              </button>
            </div>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div
            style={{
              backgroundColor: "#f6f7fb",
              borderBottom: "1px solid #d0d4e4",
              padding: "12px 24px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                gap: 10,
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: "#676879",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Group
                </label>
                <select
                  value={filters.group}
                  onChange={(e) =>
                    setFilters({ ...filters, group: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "5px 8px",
                    fontSize: 13,
                    border: "1px solid #d0d4e4",
                    borderRadius: 4,
                    backgroundColor: "#ffffff",
                    outline: "none",
                  }}
                >
                  <option value="">All</option>
                  {uniqueGroups.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </div>


              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: "#676879",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Owner
                </label>
                <select
                  value={filters.owner}
                  onChange={(e) =>
                    setFilters({ ...filters, owner: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "5px 8px",
                    fontSize: 13,
                    border: "1px solid #d0d4e4",
                    borderRadius: 4,
                    backgroundColor: "#ffffff",
                    outline: "none",
                  }}
                >
                  <option value="">All</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>


              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: "#676879",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Type
                </label>
                <select
                  value={filters.type}
                  onChange={(e) =>
                    setFilters({ ...filters, type: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "5px 8px",
                    fontSize: 13,
                    border: "1px solid #d0d4e4",
                    borderRadius: 4,
                    backgroundColor: "#ffffff",
                    outline: "none",
                  }}
                >
                  <option value="">All</option>
                  <option value="Personal">Personal</option>
                  <option value="Corporate">Corporate</option>
                </select>
              </div>

              {hasActiveFilters && (
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={clearFilters}
                    style={{
                      padding: "5px 12px",
                      fontSize: 13,
                      border: "none",
                      borderRadius: 4,
                      backgroundColor: "#e44258",
                      color: "#ffffff",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Client Status filter (replaces the old radio bar). The Set is
                bound live; click Apply to dismiss the panel. */}
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #d0d4e4" }}>
              <label
                style={{
                  fontSize: 11,
                  color: "#676879",
                  display: "block",
                  marginBottom: 6,
                  fontWeight: 600,
                }}
              >
                Client status
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { key: "active", label: "Active" },
                  { key: "completed", label: "Completed" },
                  { key: "inactive", label: "Inactive" },
                ].map((opt) => {
                  const checked = clientStatusFilterSet.has(opt.key);
                  return (
                    <label
                      key={opt.key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 10px",
                        background: checked ? "#e6f4ff" : "#ffffff",
                        border: `1px solid ${checked ? "#0073ea" : "#d0d4e4"}`,
                        borderRadius: 4,
                        cursor: "pointer",
                        fontSize: 13,
                        color: checked ? "#0073ea" : "#323338",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setClientStatusFilterSet((prev) => {
                            const next = new Set(prev);
                            if (checked) next.delete(opt.key);
                            else next.add(opt.key);
                            return next;
                          });
                        }}
                        style={{ margin: 0 }}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Footer: Apply / Reset / Close */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: "1px solid #d0d4e4",
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                onClick={() => {
                  // Reset both filters and status filter to what they were when
                  // the panel was opened (the draft snapshot).
                  if (filterDraft) setFilters(filterDraft);
                  if (statusDraft) setClientStatusFilterSet(statusDraft);
                }}
                style={{
                  padding: "6px 12px",
                  fontSize: 13,
                  border: "1px solid #d0d4e4",
                  borderRadius: 4,
                  background: "#ffffff",
                  cursor: "pointer",
                  color: "#323338",
                }}
              >
                Reset
              </button>
              <button
                onClick={() => {
                  // Cancel: revert to draft and close
                  if (filterDraft) setFilters(filterDraft);
                  if (statusDraft) setClientStatusFilterSet(statusDraft);
                  setShowFilters(false);
                }}
                style={{
                  padding: "6px 12px",
                  fontSize: 13,
                  border: "1px solid #d0d4e4",
                  borderRadius: 4,
                  background: "#ffffff",
                  cursor: "pointer",
                  color: "#323338",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Apply: just close (changes already live-bound)
                  setShowFilters(false);
                }}
                style={{
                  padding: "6px 12px",
                  fontSize: 13,
                  border: "none",
                  borderRadius: 4,
                  background: "#0073ea",
                  color: "#ffffff",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Apply
              </button>
            </div>
          </div>
        )}


        {/* Table - COMPLETE WITH ALL TAX COLUMNS */}
        {OTHER_KINDS.has(activeTab) ? (
          <OtherTasksTable
            // Remount per tab: the new-task row's Type starts differently.
            key={activeTab}
            kind={activeTab}
            hiddenStatuses={hiddenStatuses}
            clients={allClients}
            employees={employees}
            statusOptions={statuses.general}
            query={query}
            filters={filters}
            personFilter={personFilter}
            clientStatusFilterSet={clientStatusFilterSet}
            tasks={otherTasks}
            setTasks={setOtherTasks}
            taskTypes={otherTaskTypes}
            setTaskTypes={setOtherTaskTypes}
            loading={otherLoading}
            reload={reloadOtherTasks}
          />
        ) : (
          <>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#ffffff" }}>
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", overflowX: "auto", backgroundColor: "#ffffff" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              {renderedCols.map((colKey) => (
                <col
                  key={colKey}
                  style={{ width: columnWidths[colKey] ?? 120, minWidth: 30 }}
                />
              ))}
            </colgroup>

            <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr
                style={{
                  backgroundColor: "#f6f7fb",
                  height: 36,
                  borderBottom: "2px solid #d0d4e4",
                }}
              >
                <th
                  style={{
                    padding: "0 8px",
                    textAlign: "right",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#f6f7fb",
                    zIndex: 14,
                  }}
                  title="Position in the current view — renumbers when you sort or filter"
                >
                  #
                </th>
                <th
                  style={{
                    padding: "0 8px",
                    textAlign: "center",
                    position: "sticky",
                    left: columnWidths.rowNum,
                    backgroundColor: "#f6f7fb",
                    zIndex: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      sortedClients.length > 0 &&
                      sortedClients.every((c) => selectedRowIds.has(String(c.id)))
                    }
                    onChange={() => toggleSelectAll(sortedClients)}
                  />
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "sticky",
                    left: columnWidths.rowNum + columnWidths.checkbox,
                    backgroundColor: "#f6f7fb",
                    zIndex: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      position: "relative",
                    }}
                  >
                    <span
                      draggable
                      onDragStart={() => setDraggingCol("name")}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (draggingCol) moveColumn(draggingCol, "name");
                        setDraggingCol(null);
                      }}
                      style={{
                        cursor: "grab",
                        userSelect: "none",
                        fontSize: 16,
                      }}
                      title="Drag to reorder"
                    >
                      ⋮⋮
                    </span>
                    <span
                      style={{
                        flex: 1,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                      onClick={() => handleSort("name")}
                    >
                      Name
                      {sortField === "name" && (
                        <span style={{ fontSize: 10 }}>
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </span>
                    <ResizeHandle
                      columnKey="name"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 4px",
                    textAlign: "center",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span
                      onClick={() => handleSort("priority")}
                      title="Priority"
                      style={{
                        cursor: "pointer",
                        color: "#e44258",
                        fontWeight: 700,
                        fontSize: 16,
                        lineHeight: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      !
                      {sortField === "priority" && (
                        <span style={{ fontSize: 10 }}>
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </span>
                    <ResizeHandle
                      columnKey="priority"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SortableHeader
                      field="task"
                      label="Task"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <ResizeHandle
                      columnKey="task"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SortableHeader
                      field="group"
                      label="Group"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <ResizeHandle
                      columnKey="group"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "##676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span>Files</span>
                    <ResizeHandle
                      columnKey="files"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SortableHeader
                      field="owner"
                      label="Owner"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <ResizeHandle
                      columnKey="owner"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SortableHeader
                      field="status"
                      label="Status"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <ResizeHandle
                      columnKey="status"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SortableHeader
                      field="fiscal_year"
                      label="Fiscal Year"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <ResizeHandle
                      columnKey="fiscal_year"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SortableHeader
                      field="yearEnd"
                      label="Year End"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <ResizeHandle
                      columnKey="yearEnd"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SortableHeader
                      field="dueDate"
                      label="Due Date"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <ResizeHandle
                      columnKey="dueDate"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SortableHeader
                      field="completed"
                      label="Completed"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <ResizeHandle
                      columnKey="completed"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                  title="What became of this file the following year"
                >
                  Next Year
                  <ResizeHandle
                    columnKey="nextYear"
                    onResizeStart={handleResizeStart}
                  />
                </th>
                {activeTab !== "personal" && docsReceivedHeader()}
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SortableHeader
                      field="type"
                      label="Type"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <ResizeHandle
                      columnKey="type"
                      onResizeStart={handleResizeStart}
                    />
                  </div>
                </th>

                {/* TAX COLUMNS — one set per tab. The old status dropdowns
                    (GST, Cap. Div., CRA Auth, Efile, CDA, P-GST) read Client
                    fields off Task rows and always rendered blank; they are
                    gone. Time / Invoiced / A/R are gone too. */}
                {activeTab === "personal" ? (
                  <>
                    {taxHeader("T1135", "t1135")}
                    {docsReceivedHeader()}
                  </>
                ) : activeTab === "corporate" ? (
                  <>
                    {taxHeader("T4", "t4_slips")}
                    {taxHeader("T5", "t5_slips")}
                    {taxHeader("T1135", "t1135")}
                  </>
                ) : activeTab === "tslips" ? (
                  <>
                    {taxHeader("T4", "t4_slips")}
                    {taxHeader("T5", "t5_slips")}
                    {taxHeader("T1135", "t1135")}
                  </>
                ) : null}

                {/* Actions Column — width comes from the "actions" <col> */}
                <th
                  style={{
                    padding: "0 12px",
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#676879",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              <VirtualTableRows
                count={groupedRows.length}
                scrollRef={scrollRef}
                colSpan={renderedCols.length}
                estimateSize={36}
              >
                {(index, measureRef) => {
                  const entry = groupedRows[index];
                  const idx = index;
                if (entry.kind === "header") {
                  const isCollapsed = collapsedGroups.has(entry.label);
                  return (
                    <tr
                      key={`group-${entry.label}-${idx}`}
                      ref={measureRef}
                      data-index={index}
                      onClick={() => toggleGroup(entry.label)}
                      style={{
                        background: "#f0f3f8",
                        cursor: "pointer",
                        height: 32,
                      }}
                    >
                      <td
                        colSpan={renderedCols.length}
                        style={{
                          padding: "6px 16px",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#323338",
                          letterSpacing: "0.3px",
                          position: "sticky",
                          left: 0,
                        }}
                      >
                        <span style={{ color: "#0073ea", marginRight: 8 }}>
                          {isCollapsed ? "▶" : "▼"}
                        </span>
                        {entry.label}{" "}
                        <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                          ({entry.count})
                        </span>
                      </td>
                    </tr>
                  );
                }
                const c = entry.client;
                const rowColor = getRowStatusColor(c);
                const isCorp = c.task_category ? c.task_category === "corporate" : !!c.is_corporate;

                return (
                  <tr
                    key={c.id}
                    ref={measureRef}
                    data-index={index}
                    style={{
                      backgroundColor: rowColor.bg,
                      height: 36,
                      cursor: "pointer",
                      borderBottom: "1px solid rgba(255,255,255,0.15)",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.filter = "brightness(0.95)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.filter = "brightness(1)")
                    }
                  >
                    {/* Row number - positional, renumbers with the view */}
                    <td
                      style={{
                        padding: "0 8px",
                        textAlign: "right",
                        fontSize: 12,
                        fontVariantNumeric: "tabular-nums",
                        color: rowColor.text,
                        opacity: 0.65,
                        position: "sticky",
                        left: 0,
                        backgroundColor: rowColor.bg,
                        zIndex: 11,
                      }}
                    >
                      {rowOrdinals[index]}
                    </td>

                    {/* Checkbox */}
                    <td
                      style={{
                        padding: "0 8px",
                        position: "sticky",
                        left: columnWidths.rowNum,
                        backgroundColor: rowColor.bg,
                        zIndex: 10,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRowIds.has(String(c.id))}
                        onChange={() => toggleRowSelected(c.id)}
                      />
                    </td>

                    {/* Name - FROZEN */}
                    <td
                      style={{
                        padding: "0 12px",
                        fontSize: 13,
                        fontWeight: 500,
                        color: rowColor.text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        position: "sticky",
                        left: columnWidths.rowNum + columnWidths.checkbox,
                        backgroundColor: rowColor.bg,
                        zIndex: 9,
                        boxShadow: "2px 0 4px rgba(0,0,0,0.06)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Link
                          to={`/tasks/${c.id}`}
                          style={{
                            color: "inherit",
                            textDecoration: "none",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={c.name || c.client_name || ""}
                        >
                          {c.name || c.client_name || `Task ${c.id}`}
                        </Link>
                        <button
                          aria-label={`Notes for ${c.client_name || "this client"}`}
                          title="Notes"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedClientForUpdates(c);
                            setShowUpdatesPanel(true);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 2,
                            display: "flex",
                            alignItems: "center",
                            position: "relative",
                            opacity: 0.8,
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={rowColor.text}
                            strokeWidth="2"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                          {c.updates && c.updates.length > 0 && (
                            <span
                              style={{
                                position: "absolute",
                                top: -4,
                                right: -4,
                                backgroundColor: "#e44258",
                                color: "#ffffff",
                                borderRadius: "50%",
                                width: 14,
                                height: 14,
                                fontSize: 9,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 600,
                              }}
                            >
                              {c.updates.length}
                            </span>
                          )}
                        </button>
                      </div>
                    </td>

                    {/* Priority — a clickable "!" flag, not a dropdown */}
                    <td style={getCellStyle(c.id, "priority", rowColor)}>
                      <FlagCell
                        flagged={c.is_flagged}
                        onChange={(v) =>
                          handleFieldChange(c.id, "is_flagged", v)
                        }
                      />
                    </td>

                    {/* Task — inline TaskType picker per row */}
                    <td
                      style={getCellStyle(c.id, "task", rowColor)}
                      onMouseEnter={() => setHoveredCell(`${c.id}-task`)}
                      onMouseLeave={() => setHoveredCell(null)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {editingTaskCell === c.id ? (
                        <select
                          autoFocus
                          value={c.task_type ?? ""}
                          onChange={async (e) => {
                            const val = e.target.value;
                            if (val === "__new__") {
                              setShowNewTaskTypeModal(true);
                              setEditingTaskCell(null);
                              return;
                            }
                            const taskTypeId = val === "" ? null : parseInt(val, 10);
                            setEditingTaskCell(null);
                            try {
                              await apiPatch(`/api/tasks/${c.id}/`, { task_type: taskTypeId });
                              await fetchClients();
                            } catch (err) {
                              alert(`Failed to update task: ${err.message || err}`);
                            }
                          }}
                          onBlur={() => setEditingTaskCell(null)}
                          style={{
                            width: "100%",
                            padding: "4px 6px",
                            border: "1px solid #d1d5db",
                            borderRadius: 4,
                            fontSize: 13,
                            background: "white",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">— None —</option>
                          {taskTypeOptions.map((tt) => (
                            <option key={tt.id} value={tt.id}>{tt.name}</option>
                          ))}
                          <option value="__new__">+ New Task…</option>
                        </select>
                      ) : (
                        <div
                          onClick={() => setEditingTaskCell(c.id)}
                          style={{
                            cursor: "pointer",
                            padding: "4px 6px",
                            fontSize: 13,
                            color: c.task_type_name ? rowColor.text : "#9ca3af",
                            background:
                              hoveredCell === `${c.id}-task` ? "rgba(0,0,0,0.04)" : "transparent",
                            borderRadius: 4,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minHeight: 20,
                          }}
                          title={c.task_type_name || "Click to assign task"}
                        >
                          {c.task_type_name || "—"}
                        </div>
                      )}
                    </td>

                    {/* Group */}
                    <td
                      style={{
                        ...getCellStyle(c.id, "group", rowColor),
                        cursor: editingGroupClientId === c.id ? "text" : "pointer",
                      }}
                      onMouseEnter={() => setHoveredCell(`${c.id}-group`)}
                      onMouseLeave={() => setHoveredCell(null)}
                      onClick={() => {
                        if (editingGroupClientId !== c.id) {
                          setEditingGroupClientId(c.id);
                          setEditingGroupValue(c.group_name || "");
                        }
                      }}
                      title={editingGroupClientId === c.id ? undefined : c.group_name}
                    >
                      {editingGroupClientId === c.id ? (
                        <input
                          autoFocus
                          type="text"
                          value={editingGroupValue}
                          onChange={(e) => setEditingGroupValue(e.target.value)}
                          onBlur={() => handleGroupSave(c.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleGroupSave(c.id);
                            }
                            if (e.key === "Escape") {
                              setEditingGroupClientId(null);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: "100%",
                            border: "1.5px solid #0073ea",
                            borderRadius: 4,
                            padding: "2px 6px",
                            fontSize: "inherit",
                            fontFamily: "inherit",
                            outline: "none",
                            backgroundColor: "#fff",
                            boxSizing: "border-box",
                          }}
                        />
                      ) : (
                        c.group_name || "—"
                      )}
                    </td>

                    {/* Files with SharePoint button */}
                    <td
                      style={{
                        ...getCellStyle(c.id, "files", rowColor),
                      }}
                      onMouseEnter={() => setHoveredCell(`${c.id}-files`)}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      <div style={{ display: "flex", gap: 4 }}>
                        <button
                          onClick={() => handleFileModalOpen(c)}
                          style={{
                            padding: "4px 8px",
                            background: "rgba(255,255,255,0.2)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: 11,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            color: rowColor.text,
                          }}
                        >
                          📁 {c.file_count || 0}
                        </button>
                        <button
                          onClick={() => handleSharePointPickerOpen(c)}
                          title="Attach from SharePoint"
                          style={{
                            padding: "4px 6px",
                            background: "rgba(255,255,255,0.2)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: 12,
                            color: rowColor.text,
                          }}
                        >
                          📎
                        </button>
                      </div>
                    </td>

                    {/* Owner - Clickable */}
                    <td
                      style={{
                        ...getCellStyle(c.id, "owner", rowColor),
                      }}
                      onMouseEnter={() => setHoveredCell(`${c.id}-owner`)}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          cursor: "pointer",
                        }}
                        onClick={() => handleOwnerModalOpen(c)}
                      >
                        {(() => {
                          const ownerNames = getOwnerNames(c);

                          if (ownerNames.length > 0) {
                            return (
                              <>
                                {ownerNames.slice(0, 3).map((name, idx) => (
                                  <OwnerBubble
                                    key={idx}
                                    owner={name}
                                    size={24}
                                  />
                                ))}
                                {ownerNames.length > 3 && (
                                  <div
                                    style={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: "50%",
                                      backgroundColor: "rgba(255,255,255,0.3)",
                                      color: rowColor.text,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 10,
                                      fontWeight: 600,
                                    }}
                                  >
                                    +{ownerNames.length - 3}
                                  </div>
                                )}
                              </>
                            );
                          } else {
                            return (
                              <div
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  border: `2px dashed rgba(255,255,255,0.5)`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 16,
                                  color: rowColor.text,
                                }}
                              >
                                +
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </td>

                    {/* Status */}
                    <td
                      style={{
                        ...getCellStyle(c.id, "status", rowColor),
                      }}
                      onMouseEnter={() => setHoveredCell(`${c.id}-status`)}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      <ClickableStatus
                        clientId={c.id}
                        field="status"
                        value={c.status ?? null}
                        valueDisplay={c.status_display}
                        options={statuses.general || []}
                        onChange={(value) =>
                          handleStatusChange(c.id, "status", value)
                        }
                        placeholder="Set status"
                      />
                    </td>

                    {/* Fiscal Year Column */}
                    <td
                      key="fiscal_year"
                      style={{
                        ...getCellStyle(c.id, "fiscal_year", rowColor),
                        textAlign: "center",
                        fontFamily: "monospace",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                      onMouseEnter={() =>
                        setHoveredCell(`${c.id}-fiscal_year`)
                      }
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      {hoveredCell === `${c.id}-fiscal_year` ? (
                        <input
                          type="number"
                          min="2000"
                          max="2100"
                          value={c.fiscal_year || ""}
                          onChange={(e) =>
                            handleStatusChange(c.id, "fiscal_year", e.target.value ? parseInt(e.target.value) : null)
                          }
                          onBlur={(e) => {
                            if (e.target.value) {
                              handleStatusChange(c.id, "fiscal_year", parseInt(e.target.value));
                            }
                          }}
                          style={{
                            width: "80px",
                            padding: "4px 8px",
                            border: "1px solid #0073ea",
                            borderRadius: 4,
                            textAlign: "center",
                            fontFamily: "monospace",
                            fontSize: 14,
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.target.blur();
                            }
                          }}
                        />
                      ) : (
                        <span>
                          {c.fiscal_year || "—"}
                          {/* Show roll-forward indicator */}
                          {needsRollForward(c) && (
                            <span
                              style={{
                                marginLeft: 8,
                                backgroundColor: "#fdab3d",
                                color: "white",
                                padding: "2px 6px",
                                borderRadius: 3,
                                fontSize: 10,
                                fontWeight: 600,
                              }}
                              title="Client needs to be rolled forward"
                            >
                              ⚠
                            </span>
                          )}
                        </span>
                      )}
                    </td>

                    {/* Year End - INLINE EDITING */}
                    <td
                      style={{
                        ...getCellStyle(c.id, "year_end", rowColor),
                      }}
                      onMouseEnter={() => setHoveredCell(`${c.id}-year_end`)}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      {isCorp && hoveredCell === `${c.id}-year_end` ? (
                        <input
                          type="date"
                          value={formatDateForInput(c.year_end_date || "")}
                          onChange={(e) => {
                            handleYearEndChange(
                              c.id,
                              isCorp,
                              e.target.value
                            );
                          }}
                          onBlur={() => setHoveredCell(null)}
                          onMouseLeave={(e) => {
                            // Don't close if clicking inside the date picker
                            e.stopPropagation();
                          }}
                          autoFocus
                          style={{
                            width: "100%",
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid rgba(255,255,255,0.5)",
                            fontSize: 12,
                            outline: "none",
                            background: "rgba(255,255,255,0.9)",
                            color: "#323338",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            cursor: isCorp ? "pointer" : "default",
                            fontSize: 12,
                            textAlign: "center",
                          }}
                          onClick={() => {
                            if (isCorp) setHoveredCell(`${c.id}-year_end`);
                          }}
                        >
                          {isCorp
                            ? c.year_end_date
                              ? formatDate(c.year_end_date, "MMM DD")
                              : "Set"
                            : "Dec 31"}
                        </div>
                      )}
                    </td>

                    {/* Due Date - INLINE EDITING */}
                    <td
                      style={{
                        ...getCellStyle(c.id, "due_date", rowColor),
                      }}
                      onMouseEnter={() => setHoveredCell(`${c.id}-due_date`)}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      {hoveredCell === `${c.id}-due_date` ? (
                        <input
                          type="date"
                          value={formatDateForInput(c.due_date || "")}
                          onChange={(e) => {
                            handleDateChange(c.id, "due_date", e.target.value);
                          }}
                          onBlur={() => setHoveredCell(null)}
                          onMouseLeave={(e) => {
                            e.stopPropagation();
                          }}
                          autoFocus
                          style={{
                            width: "100%",
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid rgba(255,255,255,0.5)",
                            fontSize: 12,
                            outline: "none",
                            background: "rgba(255,255,255,0.9)",
                            color: "#323338",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            cursor: "pointer",
                            fontSize: 12,
                            textAlign: "center",
                          }}
                          onClick={() => setHoveredCell(`${c.id}-due_date`)}
                        >
                          {c.due_date
                            ? formatDate(c.due_date, "MMM DD")
                            : "Set"}
                        </div>
                      )}
                    </td>

                    {/* Completed — the date the task was finished. Stamped by
                        the server on completion (either path), and editable
                        here so a wrong date can be corrected. Rows finished
                        before this column existed show "Set" unless the
                        backfill found an efile date for them. */}
                    <td
                      style={{
                        ...getCellStyle(c.id, "completed_date", rowColor),
                      }}
                      onMouseEnter={() =>
                        setHoveredCell(`${c.id}-completed_date`)
                      }
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      {hoveredCell === `${c.id}-completed_date` ? (
                        <input
                          type="date"
                          value={formatDateForInput(c.completed_date || "")}
                          onChange={(e) => {
                            handleDateChange(
                              c.id,
                              "completed_date",
                              e.target.value
                            );
                          }}
                          onBlur={() => setHoveredCell(null)}
                          onMouseLeave={(e) => {
                            e.stopPropagation();
                          }}
                          autoFocus
                          style={{
                            width: "100%",
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid rgba(255,255,255,0.5)",
                            fontSize: 12,
                            outline: "none",
                            background: "rgba(255,255,255,0.9)",
                            color: "#323338",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            cursor: "pointer",
                            fontSize: 12,
                            textAlign: "center",
                            // An open task has nothing to show, and offering
                            // "Set" there would invite dating work that isn't
                            // done. Dim dash instead.
                            opacity: c.completed_date || isFinished(c) ? 1 : 0.4,
                          }}
                          onClick={() =>
                            setHoveredCell(`${c.id}-completed_date`)
                          }
                        >
                          {c.completed_date
                            ? formatDate(c.completed_date, "MMM DD")
                            : isFinished(c)
                            ? "Set"
                            : "—"}
                        </div>
                      )}
                    </td>

                    {/* Next year: rolled / waiting / won't roll. */}
                    <td
                      style={{
                        ...getCellStyle(c.id, "next_year", rowColor),
                        textAlign: "center",
                      }}
                    >
                      <NextYearCell
                        row={c}
                        onRoll={(row) => setRollTask(row)}
                        onStop={handleRollStop}
                      />
                    </td>

                    {/* Docs Rec'd — moves right of T1135 on the Personal tab */}
                    {activeTab !== "personal" &&
                      docsReceivedCell(c, rowColor)}

                    {/* Type. On the Personal tab this is the per-task type
                        (personal / biz / dead), which stamps the due date.
                        Elsewhere it stays the client's corporate/personal
                        label, which on a single-category tab is constant. */}
                    <td
                      style={{
                        ...getCellStyle(c.id, "type", rowColor),
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                      onMouseEnter={() => setHoveredCell(`${c.id}-type`)}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      {activeTab === "personal" ? (
                        <select
                          value={c.personal_type || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              c.id,
                              "personal_type",
                              e.target.value
                            )
                          }
                          style={{
                            width: "100%",
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid rgba(255,255,255,0.5)",
                            fontSize: 12,
                            outline: "none",
                            background: "rgba(255,255,255,0.9)",
                            color: "#323338",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">—</option>
                          <option value="personal">Personal</option>
                          <option value="biz">Biz</option>
                          <option value="dead">Dead</option>
                        </select>
                      ) : activeTab === "corporate" ? (
                        <select
                          value={c.corporate_type || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              c.id,
                              "corporate_type",
                              e.target.value
                            )
                          }
                          style={{
                            width: "100%",
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: "1px solid rgba(255,255,255,0.5)",
                            fontSize: 12,
                            outline: "none",
                            background: "rgba(255,255,255,0.9)",
                            color: "#323338",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">—</option>
                          <option value="t2_only">T2 only</option>
                          <option value="fs">FS</option>
                          <option value="comp">Comp</option>
                        </select>
                      ) : (
                        <>{isCorp ? "Corp" : "Personal"}</>
                      )}
                    </td>

                    {/* TAX COLUMNS — one set per tab */}
                    {activeTab === "personal" ? (
                      <>
                        <td style={getCellStyle(c.id, "t1135", rowColor)}>
                          <CheckboxCell
                            checked={c.t1135}
                            onChange={(v) => handleSlipToggle(c, "t1135", v)}
                          />
                        </td>
                        {docsReceivedCell(c, rowColor)}
                      </>
                    ) : activeTab === "corporate" ? (
                      <>
                        <td style={getCellStyle(c.id, "t4_slips", rowColor)}>
                          <CheckboxCell
                            checked={c.t4_slips}
                            onChange={(v) => handleSlipToggle(c, "t4_slips", v)}
                          />
                        </td>
                        <td style={getCellStyle(c.id, "t5_slips", rowColor)}>
                          <CheckboxCell
                            checked={c.t5_slips}
                            onChange={(v) => handleSlipToggle(c, "t5_slips", v)}
                          />
                        </td>
                        <td style={getCellStyle(c.id, "t1135", rowColor)}>
                          <CheckboxCell
                            checked={c.t1135}
                            onChange={(v) => handleSlipToggle(c, "t1135", v)}
                          />
                        </td>
                      </>
                    ) : activeTab === "tslips" ? (
                      <>
                        <td style={getCellStyle(c.id, "t4_slips", rowColor)}>
                          <CheckboxCell
                            checked={c.t4_slips}
                            onChange={(v) => handleFieldChange(c.id, "t4_slips", v)}
                          />
                        </td>
                        <td style={getCellStyle(c.id, "t5_slips", rowColor)}>
                          <CheckboxCell
                            checked={c.t5_slips}
                            onChange={(v) => handleFieldChange(c.id, "t5_slips", v)}
                          />
                        </td>
                        <td style={getCellStyle(c.id, "t1135", rowColor)}>
                          <CheckboxCell
                            checked={c.t1135}
                            onChange={(v) => handleFieldChange(c.id, "t1135", v)}
                          />
                        </td>
                      </>
                    ) : null}

                    {/* Actions */}
                    <td
                      style={{
                        padding: "0 12px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTask(c);
                        }}
                        style={{
                          padding: "2px 4px",
                          background: "rgba(228, 66, 88, 0.15)",
                          border: "1px solid rgba(228, 66, 88, 0.3)",
                          borderRadius: 4,
                          cursor: "pointer",
                          fontSize: 8,
                          color: "#e44258",
                          fontWeight: 200,
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "rgba(228, 66, 88, 0.25)";
                          e.currentTarget.style.borderColor =
                            "rgba(228, 66, 88, 0.5)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            "rgba(228, 66, 88, 0.15)";
                          e.currentTarget.style.borderColor =
                            "rgba(228, 66, 88, 0.3)";
                        }}
                        title="Delete task"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
                }}
              </VirtualTableRows>
            </tbody>
          </table>

          {/* Empty State */}
          {sortedClients.length === 0 && (
            <div
              style={{
                padding: "60px 20px",
                textAlign: "center",
                color: "#676879",
              }}
            >
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#d0d4e4"
                strokeWidth="1.5"
                style={{ margin: "0 auto 16px" }}
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <p style={{ fontSize: 16, fontWeight: 500, margin: "0 0 8px" }}>
                No clients found
              </p>
              <p style={{ fontSize: 13, margin: 0 }}>
                Try adjusting your filters or search
              </p>
            </div>
          )}
        </div>
        </div>
          </>
        )}
      </div>

      {rollTask && (
        <RollForwardFlow
          task={rollTask}
          employees={employees}
          onClose={() => setRollTask(null)}
          onRolled={(next, { archivedSource }) => {
            const oldId = rollTask.id;
            setRollTask(null);
            // Put the new year's task in the grid and, if it was archived,
            // take the old one out -- without reloading every task. The old
            // one's Next year marker now points at the new task.
            setClients((prev) => [
              ...prev
                .filter((c) => !(archivedSource && c.id === oldId))
                .map((c) => (c.id === oldId
                  ? { ...c, next_year: { state: "rolled", task_id: next.id,
                                         fiscal_year: next.fiscal_year, reason: "" } }
                  : c)),
              ...normalizeRows([next]),
            ]);
          }}
        />
      )}

      {/* Updates Side Panel */}
      {showUpdatesPanel && selectedClientForUpdates && (
        <ClientUpdatesPanel
          // A grid row is a TASK; notes belong to its CLIENT. This used to
          // pass the row's own id, so the bubble read and wrote the notes of
          // whichever client happened to share the task's number.
          clientId={selectedClientForUpdates.client}
          user={user}
          onClose={() => {
            setShowUpdatesPanel(false);
            setSelectedClientForUpdates(null);
          }}
        />
      )}

      {/* New Client Modal */}
      {showNewClientModal && (
        <NewClientModal
          onClose={() => setShowNewClientModal(false)}
          onSave={handleCreateClient}
          employees={employees}
          statuses={statuses}
        />
      )}

      {/* Create Task Modal */}
      {showCreateTaskModal && (
        <CreateTaskModal
          taskTypeOptions={taskTypeOptions}
          onClose={() => setShowCreateTaskModal(false)}
          onCreated={async () => {
            setShowCreateTaskModal(false);
            await fetchClients();
          }}
        />
      )}

      {/* Existing Modals */}
      {fileModalOpen && selectedClientForFiles && (
        <FileUploadModal
          isOpen={fileModalOpen}
          clientId={selectedClientForFiles.id}
          files={selectedClientForFiles.files || []}
          onClose={() => {
            setFileModalOpen(false);
            setSelectedClientForFiles(null);
          }}
          onFileAdded={handleFileAdded}
          onFileRemoved={handleFileRemoved}
          onOpenSharePointPicker={() => {
            // Keep selectedClientForFiles intact — don't null it here.
            // The picker needs it to know which client to attach files to.
            setFileModalOpen(false);
            setSharePointPickerOpen(true);
          }}
        />
      )}

      <RollForwardModal
        scope={rollForwardScope}
        onClose={() => {
          setRollForwardScope(null);
          // A run creates next year's tasks and may archive this year's;
          // reload so the grid shows both.
          fetchClients();
        }}
      />

      {/* New Task Type modal (quick-add from the bulk-assign toolbar) */}
      {showNewTaskTypeModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowNewTaskTypeModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: 8,
              padding: 24,
              minWidth: 320,
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16 }}>
              New Task
            </h3>
            <NewTaskTypeForm
              onCancel={() => setShowNewTaskTypeModal(false)}
              onSaved={async () => {
                setShowNewTaskTypeModal(false);
                try {
                  const list = await getTaskTypes();
                  setTaskTypeOptions(Array.isArray(list) ? list : []);
                } catch {
                  /* swallow refresh failure */
                }
              }}
            />
          </div>
        </div>
      )}

      {sharePointPickerOpen && selectedClientForFiles && (
        <SharePointFilePicker
          isOpen={sharePointPickerOpen}
          clientId={selectedClientForFiles.id}
          clientName={selectedClientForFiles.name}
          onClose={() => {
            setSharePointPickerOpen(false);
            setSelectedClientForFiles(null);
          }}
          onFileSelected={handleSharePointFileSelected}
        />
      )}

      {/* Owner Modal */}
      {showOwnerModal && selectedClientForOwner && (
        <OwnerPickerModal
          employees={employees}
          initialIds={selectedOwners}
          onSave={handleOwnerSave}
          onClose={() => setShowOwnerModal(false)}
        />
      )}

      {/* Error Message */}
      {err && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            backgroundColor: "#e44258",
            color: "#ffffff",
            padding: "12px 16px",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1000,
          }}
        >
          {err}
        </div>
      )}

      {/* Status Dropdown Portal */}
      {openStatusMenu?.rect &&
        createPortal(
          <div
            data-status-menu
            onMouseDown={(e) => e.stopPropagation()}
            style={(() => {
              const DROPDOWN_HEIGHT = 300;
              const spaceBelow = window.innerHeight - openStatusMenu.rect.bottom;
              const openAbove = spaceBelow < DROPDOWN_HEIGHT + 10;
              return {
                position: "fixed",
                ...(openAbove
                  ? { bottom: window.innerHeight - openStatusMenu.rect.top + 6 }
                  : { top: openStatusMenu.rect.bottom + 6 }),
                left: openStatusMenu.rect.left,
                width: Math.max(openStatusMenu.rect.width, 200),
                background: "white",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
                zIndex: 999999,
                overflow: "hidden",
                maxHeight: DROPDOWN_HEIGHT,
                overflowY: "auto",
              };
            })()}
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              console.log("Portal rendering with stored data");

              const options = openStatusMenu.options || [];
              const currentValue = openStatusMenu.value;
              const handleChange = openStatusMenu.onChange;

              if (!handleChange) {
                console.error("No onChange handler stored!");
                return <div style={{ padding: 12 }}>Error: No handler</div>;
              }

              if (options.length === 0) {
                return (
                  <div style={{ padding: 12, color: "#9ca3af" }}>
                    No options available
                  </div>
                );
              }

              console.log("Rendering", options.length, "options");

              return options.map((opt) => (
                <div
                  key={opt.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("Option clicked:", opt.name, "ID:", opt.id);
                    handleChange(opt.id);
                    setOpenStatusMenu(null);
                  }}
                  style={{
                    padding: "10px 12px",
                    cursor: "pointer",
                    background: currentValue === opt.id ? "#f6f7fb" : "white",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f6f7fb")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      currentValue === opt.id ? "#f6f7fb" : "white")
                  }
                >
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 2,
                      background: opt.color || "#9ca3af",
                    }}
                  />
                  {opt.name}
                </div>
              ));
            })()}
          </div>,
          document.body
        )}
    </div>
  );
}
